import React from 'react';

import {FloatingPopover, useFloating} from '@pb/kit-component-floating';
import {withCssModule} from '@pb/kit-util-with-css-module';

import styles from './tooltip.component.module.styl';

import type {FloatingPopoverProps, UseFloatingProps, UseFloatingReturnType} from '@pb/kit-component-floating';
import type {ReactNode} from 'react';


const classNames = withCssModule(styles);

type _FloatingPopoverProps = Partial<Omit<FloatingPopoverProps, 'open' | 'listItemsRef' | 'getContext' | 'flip'>>;

export interface TooltipProps extends UseFloatingProps {

    /**
     * Visual theme of the tooltip bubble. `"dark"` (default) uses an inverted
     * dark background; `"light"` uses a light/white background for dark-on-light
     * contexts.
     */
    color?: 'light' | 'dark',

    /**
     * Render prop for the element that triggers the tooltip on hover or focus.
     * Receives `anchorRef` (attach to your element), `getAnchorProps` (spread onto
     * it to register hover/focus handlers and `aria-describedby`), and `positionRef`
     * (optional — attach to a child if you want positioning relative to a sub-element
     * rather than the full anchor bounds).
     */
    anchor: (props: {
        anchorRef: UseFloatingReturnType['anchorRef'],
        /** Merges hover, focus, and ARIA props onto an arbitrary set of element props. */
        getAnchorProps: UseFloatingReturnType['getAnchorProps'],
        positionRef: UseFloatingReturnType['positionRef'],
    }) => ReactNode,

    /**
     * Props forwarded directly to the `<FloatingPopover>` bubble element (e.g.
     * `className`, `style`). `open`, `listItemsRef`, `getContext`, and `flip` are
     * managed internally and cannot be overridden.
     */
    popoverProps?: _FloatingPopoverProps,

    /**
     * Content rendered inside the tooltip bubble. Should be short — typically plain
     * text or a brief formatted label. Avoid interactive elements (links, buttons)
     * since tooltips are not focusable; use a `<Dropdown>` for clickable overlays.
     */
    children: ReactNode,
}


/**
 * Accessible tooltip that appears on hover or keyboard focus and describes the
 * anchor element via `aria-describedby`.
 *
 * Attach to any element through the required `anchor` render prop, which receives
 * `anchorRef`, `getAnchorProps`, and `positionRef`. The bubble is positioned above
 * the anchor by default (8 px offset); pass `placement` or `allowedPlacements` to
 * change it.
 *
 * ## Features
 * - Shows on pointer enter / keyboard focus; hides on pointer leave / blur / Escape.
 * - Auto-flips to the opposite side when viewport space is insufficient.
 * - Focus is not trapped inside the tooltip (`FloatingFocusManager` disabled).
 *
 * ## Accessibility
 * Implements `role="tooltip"` and `aria-describedby` per the
 * [WAI-ARIA Tooltip Pattern](https://www.w3.org/WAI/ARIA/apg/patterns/tooltip/).
 * Tooltip content must be text-only or equivalent — do not place focusable elements
 * inside it (use `<Dropdown>` for interactive overlays instead).
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/hover-tooltip--documentation}
 */
export function Tooltip({
    anchor,
    children,
    popoverProps,
    color = 'dark',
    allowedPlacements,
    placement = allowedPlacements ? undefined : 'top',
    ...restProps
}: TooltipProps) {

    const {
        anchorRef, floatingElRef, getAnchorProps, getFloatingElProps, positionRef,
    } = useFloating({
        hoverEnabled: true,
        focusEnabled: true,
        role: 'tooltip',
        placement,
        allowedPlacements,
        verticalOffset: 8,
        ...restProps,
    });

    return (
        <>
            {anchor({
                anchorRef,
                getAnchorProps,
                positionRef,
            })}
            <FloatingPopover
                floatingFocusManagerProps={{disabled: true}}
                {...getFloatingElProps({
                    ...popoverProps,
                    className: classNames('tooltip', popoverProps?.className, {
                        tooltip_dark: color === 'dark',
                    }),
                })}
                ref={floatingElRef}>
                {children}
            </FloatingPopover>
        </>
    );
}
